/**
 * Web stub for audioProSetup.
 *
 * On web, there are no native lock screen controls or remote commands,
 * but we still need to wire engine events (e.g. track ended) into the player
 * store so queue behaviour matches native.
 */

import type { Player } from "../createPlayer";
import type { AudioPlayer } from "./AudioPlayer.web";

type PlaybackAudio = Pick<AudioPlayer, "onTrackEnded" | "seekTo">;
const attachments = new WeakMap<PlaybackAudio, () => void>();

export function setupAudioPro(
  player: Pick<Player, "store" | "lifecycle"> & { audio: PlaybackAudio },
): () => void {
  const audioPlayer = player.audio;
  const attached = attachments.get(audioPlayer);
  if (attached) return attached;

  // Match native behaviour: when a track ends, either repeat or advance.
  const offEnded = audioPlayer.onTrackEnded(() => {
    const state = player.store.getState();
    const queue =
      state.activeQueueType === "playlist"
        ? state.playlistQueue
        : state.foryouQueue;
    player.lifecycle.end(
      state.repeatMode === "off" &&
        queue &&
        queue.currentIndex === queue.items.length - 1
        ? "queue_end"
        : "track_change",
    );

    // IMPORTANT: On web the audio element often emits a 'pause' at end-of-track,
    // which can put the store in PAUSED. If we then call `actions.play()`, the
    // PAUSED shortcut will resume the *old* track instead of loading the next.
    // Force IDLE so `actions.play()` loads the newly-selected queue item.
    state.actions.setStatus("IDLE");

    if (state.repeatMode === "track") {
      audioPlayer.seekTo(0);
      state.actions.play().catch(console.error);
      return;
    }

    state.actions.next();

    // If queue exhausted (next() set userIntent to paused), seek to start
    // and override the IDLE status back to PAUSED so the UI doesn't show buffering.
    if (player.store.getState().userIntent === "paused") {
      audioPlayer.seekTo(0);
      state.actions.setStatus("PAUSED");
    }
  });

  const detach = () => {
    offEnded();
    attachments.delete(audioPlayer);
  };
  attachments.set(audioPlayer, detach);
  return detach;
}
